import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, QrCode, CheckCircle, XCircle, AlertCircle, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { mockEvents, mockScanLogs } from '@/lib/mockData';
import { formatNumber } from '@/lib/utils';

export function AdminCheckinScanner() {
  const { eventId } = useParams();
  const event = mockEvents.find(e => e.id === eventId) || mockEvents[0];
  const [searchQuery, setSearchQuery] = useState('');

  const stats = {
    total: event.ticketsSold,
    checkedIn: Math.floor(event.ticketsSold * 0.65),
    pending: Math.floor(event.ticketsSold * 0.35)
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'invalid': return <XCircle className="w-5 h-5 text-destructive" />;
      case 'already_scanned': return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      default: return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'valid': return 'Valid Entry';
      case 'invalid': return 'Invalid Ticket';
      case 'already_scanned': return 'Already Scanned';
      default: return status;
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <Link to={`/admin/events/${eventId}`} className="inline-flex items-center text-muted-foreground hover:text-white mb-6">
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Event
        </Link>

        <h1 className="text-3xl font-bold text-white mb-2">Check-in Scanner</h1>
        <p className="text-muted-foreground mb-8">{event.title}</p>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-card border-border p-6">
            <div className="text-center">
              <p className="text-4xl font-bold text-white">{formatNumber(stats.total)}</p>
              <p className="text-muted-foreground">Total Tickets</p>
            </div>
          </Card>

          <Card className="bg-card border-border p-6">
            <div className="text-center">
              <p className="text-4xl font-bold text-green-500">{formatNumber(stats.checkedIn)}</p>
              <p className="text-muted-foreground">Checked In</p>
            </div>
          </Card>

          <Card className="bg-card border-border p-6">
            <div className="text-center">
              <p className="text-4xl font-bold text-yellow-500">{formatNumber(stats.pending)}</p>
              <p className="text-muted-foreground">Pending</p>
            </div>
          </Card>
        </div>

        {/* Progress Bar */}
        <Card className="bg-card border-border p-6 mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white font-medium">Check-in Progress</span>
            <span className="text-primary font-semibold">{Math.round((stats.checkedIn / stats.total) * 100)}%</span>
          </div>
          <div className="w-full h-4 bg-secondary rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${(stats.checkedIn / stats.total) * 100}%` }}
            />
          </div>
        </Card>

        {/* Scanner Input */}
        <Card className="bg-card border-border p-6 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-lg bg-primary/20 flex items-center justify-center">
              <QrCode className="w-8 h-8 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-white font-medium mb-2">Scan or Enter Ticket Code</p>
              <div className="flex gap-2">
                <Input 
                  placeholder="Enter ticket code..."
                  className="bg-secondary border-border"
                />
                <Button className="bg-primary text-primary-foreground">
                  Verify
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Scan Log */}
        <Card className="bg-card border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">Scan Log</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input 
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-secondary border-border pl-10 w-64"
              />
            </div>
          </div>
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {mockScanLogs.map(log => (
              <div key={log.id} className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                <div className="flex items-center gap-3">
                  {getStatusIcon(log.status)}
                  <div>
                    <p className="font-medium text-white">{log.name}</p>
                    <p className="text-sm text-muted-foreground">Ticket: {log.ticketId}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-medium ${
                    log.status === 'valid' ? 'text-green-500' : 
                    log.status === 'invalid' ? 'text-destructive' : 'text-yellow-500'
                  }`}>
                    {getStatusText(log.status)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(log.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}

export default AdminCheckinScanner;
